import { useId, useState } from 'react';
import { Button, Icon, Input, Label, cn } from '@tutero/ui';

/**
 * The parts the sign-in surface draws, written once so neither view invents its own
 * (`staff-sign-in-here` D6, D7). Everything on screen is `@tutero/ui`'s (A5); the eye
 * toggle is the one exception, because `@tutero/ui@0.2.0` publishes no `PasswordInput`.
 */
export interface AuthFieldProps extends Omit<
  React.ComponentProps<typeof Input>,
  'id' | 'size'
> {
  label: string;
  /** Draw the password's show/hide control, and start obscured. */
  password?: boolean;
}

export function AuthField({
  label,
  password = false,
  className,
  type,
  ...props
}: AuthFieldProps) {
  const id = useId();
  const [shown, setShown] = useState(false);
  return (
    /* `text-left`: the sign-in states draw inside the shell's centred `Centered`. */
    <div className="flex flex-col gap-1.5 text-left">
      <Label htmlFor={id} className="text-bold-s">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type={password ? (shown ? 'text' : 'password') : type}
          className={cn(password && 'pr-10', className)}
          {...props}
        />
        {password ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            /* Named by what pressing it does, and `aria-pressed` says which state it is in. */
            aria-pressed={shown}
            aria-controls={id}
            onClick={() => setShown((was) => !was)}
            className="text-muted-foreground absolute top-1/2 right-0 -translate-y-1/2"
          >
            <span className="sr-only">
              {shown ? 'Hide Password' : 'Show Password'}
            </span>
            {/* `visibility` offers to show, as every password field on the web has it; Flutter has the pair inverted. */}
            <Icon name={shown ? 'visibility_off' : 'visibility'} size={18} />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The one place a refusal is said (D6). No field is marked wrong: the Worker does not say
 * which one was, and Flutter pretending it does is why a bad password reddens the email
 * box (`login_form.dart:172-174`). `role="alert"`, because a message that only appears is
 * a message a screen reader never hears.
 */
export function AuthErrorText({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="text-destructive text-bold-s text-left">
      {children}
    </p>
  );
}
